import { create } from 'zustand';
import { userPreferencesApi } from '../services/api';
import i18n, { applyDirAndLang, LANGUAGE_LOCALSTORAGE_KEY, type SupportedLanguage } from '../i18n';

// ---- Debounced API sync (localStorage = cache, DB = source of truth) ----
let _syncTimeout: ReturnType<typeof setTimeout> | null = null;
let _pendingUpdates: Record<string, unknown> = {};

function debouncedApiSync(partial: Record<string, unknown>) {
  _pendingUpdates = { ..._pendingUpdates, ...partial };
  if (_syncTimeout) clearTimeout(_syncTimeout);
  _syncTimeout = setTimeout(async () => {
    const batch = { ..._pendingUpdates };
    _pendingUpdates = {};
    try {
      await userPreferencesApi.updateUiSettings(batch);
    } catch (err) {
      console.warn('[UIStore] Failed to sync settings to API:', err);
    }
  }, 500);
}

// Mirrors IncidentTemplatePicker['prefill'] — kept here so the store
// can drive the global dialog mount in App.tsx without a circular
// import on the picker component.
export interface IncidentDialogPrefill {
  latitude?: number;
  longitude?: number;
  address?: string;
  siteId?: string;
  buildingId?: string;
  floorId?: string;
  snapshotDataUrl?: string;
  // Rectangle-only crop of the snapshot, used to feed the vision-LLM
  // when the operator clicks "Suggest description" — narrower focus
  // than the full frame produces better AI output. Set only when the
  // operator dragged a rectangle (point clicks have no crop).
  snapshotCropDataUrl?: string;
  geometry?: {
    type: 'point' | 'circle' | 'polygon';
    center?: [number, number];
    radius?: number;
    coordinates?: [number, number][];
  };
}

// Local storage keys for persisting settings
const LAYOUT_STORAGE_KEY = 'commander-dashboard-layout';
const SIDEBAR_AUTOCLOSE_KEY = 'commander-sidebar-autoclose';
const SIDEBAR_PINNED_KEY = 'commander-sidebar-pinned';
const MODULE_SETTINGS_KEY = 'commander-module-settings';
const CUSTOM_LAYOUTS_KEY = 'commander-custom-layouts';
const ACCENT_COLOR_KEY = 'commander-accent-color';
const UI_DENSITY_KEY = 'commander-ui-density';
const TABLE_PAGE_SIZE_KEY = 'commander-table-page-size';
const ANIMATIONS_KEY = 'commander-animations';
const MAP_DEFAULT_ZOOM_KEY = 'commander-map-default-zoom';
const MAP_DEFAULT_CENTER_KEY = 'commander-map-default-center';
const MAP_DEFAULT_STYLE_KEY = 'commander-map-default-style';
const MAP_DEFAULT_TILE_PROVIDER_KEY = 'commander-map-tile-provider';
const MAP_DEFAULT_FLOOR_PLAN_KEY = 'commander-map-default-floor-plan';
const MAP_MAX_ZOOM_KEY = 'commander-map-max-zoom';
const MAP_LAYER_ZOOM_SETTINGS_KEY = 'commander-map-layer-zoom-settings';
const MAP_MAX_FOV_DEPTH_KEY = 'commander-map-max-fov-depth';
const MAP_SELECTION_PLAYBACK_SEC_KEY = 'commander-map-selection-playback-sec';
const DATE_FORMAT_KEY = 'commander-date-format';
const TIME_FORMAT_KEY = 'commander-time-format';
const WEATHER_LOCATION_KEY = 'commander-weather-location';

// Type for module settings storage
type ModuleSettingsMap = Record<string, Record<string, unknown>>;

// Dashboard layout data type (simplified for storage)
export interface DashboardLayoutData {
  id: string;
  name: string;
  description: string;
  icon: string;
  columns: number;
  rows: number;
  rowTemplate?: string;
  cells: Array<{
    moduleId: string;
    row: number;
    col: number;
    rowSpan?: number;
    colSpan?: number;
  }>;
  modules: Array<{
    id: string;
    type: string;
    title?: string;
    settings?: Record<string, unknown>;
  }>;
  visibility?: 'private' | 'public' | 'shared';
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Validate a parsed module-settings value. Must be a plain object whose
// values are themselves plain objects. A corrupt value (array, string,
// null) is rejected so it can never reach the dashboard renderer.
function sanitizeModuleSettings(raw: unknown): ModuleSettingsMap {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: ModuleSettingsMap = {};
  for (const [key, val] of Object.entries(raw as Record<string, unknown>)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      out[key] = val as ModuleSettingsMap[string];
    }
  }
  return out;
}

// Helper to load module settings from localStorage
function loadModuleSettings(): ModuleSettingsMap {
  try {
    const stored = localStorage.getItem(MODULE_SETTINGS_KEY);
    return stored ? sanitizeModuleSettings(JSON.parse(stored)) : {};
  } catch {
    return {};
  }
}

// Helper to save module settings to localStorage
function saveModuleSettings(settings: ModuleSettingsMap): void {
  try {
    localStorage.setItem(MODULE_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    console.error('Failed to save module settings to localStorage');
  }
}

// Known problematic module types that can cause freezing
const PROBLEMATIC_MODULES = new Set<string>([
  // Add any module types that cause issues here
]);

// Valid module types for validation. (Self-contained — the legacy
// components/dashboard module framework was removed; IOSentra's editable
// dashboard now uses components/dashboard-grid.)
const VALID_MODULE_TYPES = new Set<string>([
  'alarms', 'alarms-list', 'incidents', 'devices', 'cameras', 'map', 'events', 'event-handling',
  'active-events', 'alarm-response', 'system-health', 'statistics', 'activity', 'sop-queue',
  'quick-actions', 'weather', 'clock', 'web-viewer', 'gauge', 'graph', 'threat-level',
  'access-control', 'ai-insights', 'trends', 'heatmap', 'stalled-incidents', 'operator-performance',
  'device-health-summary', 'predictions', 'kpi-cards', 'hmi-graphic', 'fleet-map', 'fleet-status',
  'fleet-events', 'incident-command', 'patrol-status', 'patrol-kpi',
]);

// Helper to validate a layout doesn't have problematic configurations
function isLayoutSafe(layout: DashboardLayoutData): boolean {
  // Check basic structure
  if (!layout.id || !layout.name || !layout.columns || !layout.rows) {
    console.warn(`Invalid layout structure: missing required fields`);
    return false;
  }

  // Check for reasonable grid size (prevent huge grids that could freeze browser)
  if (layout.columns > 10 || layout.rows > 10) {
    console.warn(`Layout "${layout.name}" has unreasonable grid size: ${layout.columns}x${layout.rows}`);
    return false;
  }

  // Check cells array
  if (!Array.isArray(layout.cells) || !Array.isArray(layout.modules)) {
    console.warn(`Layout "${layout.name}" has invalid cells or modules array`);
    return false;
  }

  // Check for too many modules (could cause performance issues)
  if (layout.modules.length > 50) {
    console.warn(`Layout "${layout.name}" has too many modules: ${layout.modules.length}`);
    return false;
  }

  // Check every module entry is well-formed and known.
  const moduleIds = new Set<string>();
  for (const mod of layout.modules) {
    if (!mod || typeof mod !== 'object' || !mod.id || !mod.type) {
      console.warn(`Layout "${layout.name}" has a malformed module entry`);
      return false;
    }
    if (PROBLEMATIC_MODULES.has(mod.type)) {
      console.warn(`Layout "${layout.name}" contains problematic module type: ${mod.type}`);
      return false;
    }
    // Validate module type exists
    if (!VALID_MODULE_TYPES.has(mod.type)) {
      console.warn(`Layout "${layout.name}" contains unknown module type: ${mod.type}`);
      return false;
    }
    moduleIds.add(mod.id);
  }

  // Check for cell position validity + that every cell references a module.
  for (const cell of layout.cells) {
    if (!cell || typeof cell !== 'object' || !cell.moduleId) {
      console.warn(`Layout "${layout.name}" has a malformed cell entry`);
      return false;
    }
    if (!moduleIds.has(cell.moduleId)) {
      console.warn(`Layout "${layout.name}" has a cell referencing missing module: ${cell.moduleId}`);
      return false;
    }
    if (cell.row < 0 || cell.col < 0 || cell.row >= layout.rows || cell.col >= layout.columns) {
      console.warn(`Layout "${layout.name}" has cell out of bounds: row=${cell.row}, col=${cell.col}`);
      return false;
    }
    // Check for excessively large spans
    if ((cell.rowSpan || 1) > layout.rows || (cell.colSpan || 1) > layout.columns) {
      console.warn(`Layout "${layout.name}" has cell with invalid span`);
      return false;
    }
  }

  return true;
}

// Validate an arbitrary value into a safe DashboardLayoutData[].
// Shared by the localStorage loader and the API-sync path so that a
// corrupt layout from EITHER source can never reach the renderer.
function sanitizeCustomLayouts(raw: unknown): DashboardLayoutData[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((l): l is DashboardLayoutData => {
    try {
      return !!l && typeof l === 'object' && isLayoutSafe(l as DashboardLayoutData);
    } catch {
      return false;
    }
  });
}

// Helper to load custom layouts from localStorage
function loadCustomLayouts(): DashboardLayoutData[] {
  try {
    const stored = localStorage.getItem(CUSTOM_LAYOUTS_KEY);
    if (!stored) return [];

    const parsed: unknown = JSON.parse(stored);
    const safeLayouts = sanitizeCustomLayouts(parsed);

    // If we filtered any out (or the stored value wasn't even an array),
    // rewrite localStorage with the cleaned list.
    const originalCount = Array.isArray(parsed) ? parsed.length : -1;
    if (safeLayouts.length !== originalCount) {
      console.warn(`Removed ${Math.max(0, originalCount - safeLayouts.length)} problematic layout(s)`);
      localStorage.setItem(CUSTOM_LAYOUTS_KEY, JSON.stringify(safeLayouts));
    }

    return safeLayouts;
  } catch (error) {
    console.error('Failed to load custom layouts, clearing storage:', error);
    localStorage.removeItem(CUSTOM_LAYOUTS_KEY);
    return [];
  }
}

// Helper to save custom layouts to localStorage
function saveCustomLayouts(layouts: DashboardLayoutData[]): void {
  try {
    localStorage.setItem(CUSTOM_LAYOUTS_KEY, JSON.stringify(layouts));
  } catch {
    console.error('Failed to save custom layouts to localStorage');
  }
}

export interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  sidebarAutoClose: boolean;
  setSidebarAutoClose: (autoClose: boolean) => void;
  // Keep the sidebar expanded (pinned) instead of hover-to-expand — device-local
  // UI pref (localStorage), so it survives reloads and doesn't depend on a mouse.
  sidebarPinned: boolean;
  setSidebarPinned: (pinned: boolean) => void;
  toggleSidebarPinned: () => void;

  // Theme
  theme: 'dark' | 'light' | 'system';
  setTheme: (theme: 'dark' | 'light' | 'system') => void;
  toggleTheme: () => void;
  getEffectiveTheme: () => 'dark' | 'light';

  // Language
  language: SupportedLanguage;
  setLanguage: (lng: SupportedLanguage) => void;

  // Appearance
  accentColor: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'cyan';
  setAccentColor: (color: 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'cyan') => void;
  uiDensity: 'compact' | 'normal' | 'comfortable';
  setUiDensity: (density: 'compact' | 'normal' | 'comfortable') => void;
  tablePageSize: number;
  setTablePageSize: (size: number) => void;
  animationsEnabled: boolean;
  setAnimationsEnabled: (enabled: boolean) => void;

  // Map settings
  mapDefaultZoom: number;
  setMapDefaultZoom: (zoom: number) => void;
  mapMaxZoom: number;
  setMapMaxZoom: (zoom: number) => void;
  mapDefaultCenter: { latitude: number; longitude: number };
  setMapDefaultCenter: (center: { latitude: number; longitude: number }) => void;
  mapDefaultStyle: 'ROADMAP' | 'SATELLITE' | 'HYBRID' | 'TERRAIN' | 'DARK' | 'LIGHT';
  setMapDefaultStyle: (style: 'ROADMAP' | 'SATELLITE' | 'HYBRID' | 'TERRAIN' | 'DARK' | 'LIGHT') => void;
  mapDefaultTileProvider: 'OSM' | 'ESRI' | 'GOOGLE' | 'CARTO';
  setMapDefaultTileProvider: (provider: 'OSM' | 'ESRI' | 'GOOGLE' | 'CARTO') => void;
  mapDefaultFloorPlanId: string | null;
  setMapDefaultFloorPlanId: (floorPlanId: string | null) => void;
  mapLayerZoomSettings: Record<string, { minZoom: number; maxZoom: number }>;
  setMapLayerZoomSettings: (settings: Record<string, { minZoom: number; maxZoom: number }>) => void;
  updateLayerZoomSetting: (layerId: string, setting: { minZoom: number; maxZoom: number }) => void;
  maxFovDepth: number;
  setMaxFovDepth: (depth: number) => void;
  selectionPlaybackSeconds: number;
  setSelectionPlaybackSeconds: (seconds: number) => void;

  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  setDateFormat: (format: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD') => void;
  timeFormat: '12h' | '24h';
  setTimeFormat: (format: '12h' | '24h') => void;

  // Weather location
  weatherLocation: { latitude: number; longitude: number; name?: string } | null;
  setWeatherLocation: (location: { latitude: number; longitude: number; name?: string } | null) => void;

  // Alarm panel
  alarmPanelVisible: boolean;
  setAlarmPanelVisible: (visible: boolean) => void;
  toggleAlarmPanel: () => void;
  alarmPanelPosition: 'right' | 'bottom' | 'hidden';
  setAlarmPanelPosition: (position: 'right' | 'bottom' | 'hidden') => void;

  // Location tree panel
  locationTreePanelVisible: boolean;
  setLocationTreePanelVisible: (visible: boolean) => void;
  toggleLocationTreePanel: () => void;

  // Live device monitor
  deviceMonitorVisible: boolean;
  setDeviceMonitorVisible: (visible: boolean) => void;
  toggleDeviceMonitor: () => void;

  // Video wall
  videoWallLayout: '1x1' | '2x2' | '3x3' | '4x4' | '1+5' | '1+7';
  setVideoWallLayout: (layout: '1x1' | '2x2' | '3x3' | '4x4' | '1+5' | '1+7') => void;

  // Dashboard layout
  dashboardLayoutId: string;
  setDashboardLayoutId: (layoutId: string) => void;
  customLayouts: DashboardLayoutData[];
  addCustomLayout: (layout: DashboardLayoutData) => void;
  updateCustomLayout: (layout: DashboardLayoutData) => void;
  deleteCustomLayout: (layoutId: string) => void;

  // Module settings - keyed by layoutId:moduleId
  moduleSettings: ModuleSettingsMap;
  getModuleSettings: (layoutId: string, moduleId: string) => Record<string, unknown> | undefined;
  setModuleSettings: (layoutId: string, moduleId: string, settings: Record<string, unknown>) => void;
  clearModuleSettings: (layoutId?: string) => void;

  // Modal state
  activeModal: string | null;
  modalData: unknown;
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;

  // Notifications
  unreadNotifications: number;
  setUnreadNotifications: (count: number) => void;
  incrementNotifications: () => void;
  clearNotifications: () => void;

  // Sound
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  soundMuted: boolean;
  toggleSoundMuted: () => void;

  // Persistent Alert Queue (G2)
  pendingAlerts: Array<{
    id: string;
    incidentId: string;
    priority: 'p1' | 'p2';
    title: string;
    message: string;
    createdAt: string;
    acknowledged: boolean;
  }>;
  addPendingAlert: (alert: { id: string; incidentId: string; priority: 'p1' | 'p2'; title: string; message: string }) => void;
  acknowledgePendingAlert: (alertId: string) => void;
  acknowledgeAllPendingAlerts: () => void;

  // Voice Commands
  voiceCommandsEnabled: boolean;
  setVoiceCommandsEnabled: (enabled: boolean) => void;
  voiceCommandAutoStop: boolean;
  setVoiceCommandAutoStop: (autoStop: boolean) => void;
  voiceCommandsPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  setVoiceCommandsPosition: (position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left') => void;

  // Chat Notifications
  chatToastEnabled: boolean;
  setChatToastEnabled: (enabled: boolean) => void;
  chatSoundEnabled: boolean;
  setChatSoundEnabled: (enabled: boolean) => void;
  chatSoundType: string;
  setChatSoundType: (soundType: string) => void;
  chatEnabled: boolean;
  setChatEnabled: (enabled: boolean) => void;
  chatPosition: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  setChatPosition: (position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left') => void;

  // Incident Notifications
  desktopNotificationsEnabled: boolean;
  setDesktopNotificationsEnabled: (enabled: boolean) => void;
  incidentToastEnabled: boolean;
  setIncidentToastEnabled: (enabled: boolean) => void;

  // Incident Toast Configuration
  incidentToastPosition: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  setIncidentToastPosition: (position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center') => void;
  incidentToastFields: {
    showPriority: boolean;
    showCategory: boolean;
    showDevice: boolean;
    showLocation: boolean;
    showStatus: boolean;
    showTime: boolean;
    showSource: boolean;
    showAssignee: boolean;
  };
  setIncidentToastFields: (fields: Partial<UIState['incidentToastFields']>) => void;
  incidentToastDuration: number;
  setIncidentToastDuration: (ms: number) => void;

  // Device Display
  treeLibraryDisplay: boolean;
  setTreeLibraryDisplay: (enabled: boolean) => void;
  badgeVisualizationMode: 'ring' | 'side_badges' | 'status_bar' | 'orbit';
  setBadgeVisualizationMode: (mode: 'ring' | 'side_badges' | 'status_bar' | 'orbit') => void;

  // Incident Badges on Device Markers
  incidentBadgesEnabled: boolean;
  setIncidentBadgesEnabled: (enabled: boolean) => void;
  incidentBadgeShowPriority: boolean;
  setIncidentBadgeShowPriority: (show: boolean) => void;
  incidentBadgeShowCount: boolean;
  setIncidentBadgeShowCount: (show: boolean) => void;
  incidentBadgeAnimation: boolean;
  setIncidentBadgeAnimation: (enabled: boolean) => void;

  // Configurable Hover Tooltip Fields
  deviceHoverFields: {
    showName: boolean;
    showCategory: boolean;
    showState: boolean;
    showBadges: boolean;
    showDriver: boolean;
    showLocation: boolean;
    showDeviceId: boolean;
    showLastEvent: boolean;
    showIncidentCount: boolean;
  };
  setDeviceHoverFields: (fields: Partial<UIState['deviceHoverFields']>) => void;

  // Configurable Incident Hover Tooltip Fields (AlarmMarkerLayer)
  incidentHoverFields: {
    showTitle: boolean;
    showPriority: boolean;
    showStatus: boolean;
    showCategory: boolean;
    showIncidentNumber: boolean;
    showLocation: boolean;
    showTimestamp: boolean;
    showType: boolean;
    showAssignee: boolean;
    showIncidentCount: boolean;
  };
  setIncidentHoverFields: (fields: Partial<UIState['incidentHoverFields']>) => void;

  // SOP Display Mode
  sopDisplayMode: 'inline' | 'wizard' | 'side-panel';
  setSopDisplayMode: (mode: 'inline' | 'wizard' | 'side-panel') => void;

  // Incident Placement Mode (transient, not persisted)
  incidentPlacementMode: boolean;
  incidentPlacementType: 'point' | 'radius' | 'area';
  setIncidentPlacementMode: (active: boolean) => void;
  setIncidentPlacementType: (type: 'point' | 'radius' | 'area') => void;
  toggleIncidentPlacementMode: () => void;

  // Global incident-creation dialog. Set by the map click handler, by
  // a camera tile click in placement mode, or by the SnapshotEditor's
  // "Create incident" button. The dialog is mounted once at the App
  // level and opens whenever this is non-null. Pending attachment is
  // a separate slot — Blobs cannot be safely persisted, so we keep
  // them transient and clear them on close.
  incidentDialogPrefill: IncidentDialogPrefill | null;
  pendingIncidentAttachment: { blob: Blob; filename: string } | null;
  openIncidentDialogWithPrefill: (
    prefill: IncidentDialogPrefill,
    attachment?: { blob: Blob; filename: string },
  ) => void;
  closeIncidentDialog: () => void;

  // API sync state
  _synced: boolean;
  _syncInProgress: boolean;
  _initFromApi: () => Promise<void>;
}

export const useUIStore = create<UIState>((set, get) => ({
  // Sidebar
  sidebarCollapsed: false,
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
  toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
  sidebarAutoClose: localStorage.getItem(SIDEBAR_AUTOCLOSE_KEY) !== 'false', // Default to true
  setSidebarAutoClose: (autoClose) => {
    localStorage.setItem(SIDEBAR_AUTOCLOSE_KEY, String(autoClose));
    set({ sidebarAutoClose: autoClose });
    debouncedApiSync({ sidebarAutoClose: autoClose });
  },
  sidebarPinned: localStorage.getItem(SIDEBAR_PINNED_KEY) === 'true', // default unpinned (hover)
  setSidebarPinned: (pinned) => {
    localStorage.setItem(SIDEBAR_PINNED_KEY, String(pinned));
    set({ sidebarPinned: pinned });
  },
  toggleSidebarPinned: () => {
    const next = !get().sidebarPinned;
    localStorage.setItem(SIDEBAR_PINNED_KEY, String(next));
    set({ sidebarPinned: next });
  },

  // Theme
  theme: (localStorage.getItem('commander-theme') as 'dark' | 'light' | 'system') || 'dark',
  setTheme: (theme) => {
    localStorage.setItem('commander-theme', theme);
    const effectiveTheme = theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;
    document.documentElement.classList.toggle('dark', effectiveTheme === 'dark');
    set({ theme });
    debouncedApiSync({ theme });
  },
  toggleTheme: () =>
    set((state) => {
      const newTheme = state.theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem('commander-theme', newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
      debouncedApiSync({ theme: newTheme });
      return { theme: newTheme };
    }),
  getEffectiveTheme: () => {
    const s = get();
    if (s.theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return s.theme;
  },

  // Language — mirrors theme pattern: localStorage cache + DB sync.
  language: ((): SupportedLanguage => {
    const stored = localStorage.getItem(LANGUAGE_LOCALSTORAGE_KEY);
    if (stored === 'en' || stored === 'he') return stored;
    return 'en';
  })(),
  setLanguage: (lng) => {
    localStorage.setItem(LANGUAGE_LOCALSTORAGE_KEY, lng);
    applyDirAndLang(lng);
    void i18n.changeLanguage(lng);
    set({ language: lng });
    debouncedApiSync({ language: lng });
  },

  // Appearance
  accentColor: (localStorage.getItem(ACCENT_COLOR_KEY) as 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'cyan') || 'blue',
  setAccentColor: (color) => {
    localStorage.setItem(ACCENT_COLOR_KEY, color);
    // Update CSS custom property for accent color
    const root = document.documentElement;
    const colors: Record<string, string> = {
      blue: '217 91% 60%',
      purple: '270 91% 65%',
      green: '142 71% 45%',
      orange: '25 95% 53%',
      red: '0 84% 60%',
      cyan: '186 94% 48%',
    };
    root.style.setProperty('--color-primary', colors[color]);
    set({ accentColor: color });
    debouncedApiSync({ accentColor: color });
  },
  uiDensity: (localStorage.getItem(UI_DENSITY_KEY) as 'compact' | 'normal' | 'comfortable') || 'normal',
  setUiDensity: (density) => {
    localStorage.setItem(UI_DENSITY_KEY, density);
    // Update CSS custom property for spacing
    const root = document.documentElement;
    const spacingScale: Record<string, string> = {
      compact: '0.875',
      normal: '1',
      comfortable: '1.125',
    };
    root.style.setProperty('--ui-density-scale', spacingScale[density]);
    set({ uiDensity: density });
    debouncedApiSync({ uiDensity: density });
  },
  tablePageSize: parseInt(localStorage.getItem(TABLE_PAGE_SIZE_KEY) || '25', 10),
  setTablePageSize: (size) => {
    localStorage.setItem(TABLE_PAGE_SIZE_KEY, String(size));
    set({ tablePageSize: size });
    debouncedApiSync({ tablePageSize: size });
  },
  animationsEnabled: localStorage.getItem(ANIMATIONS_KEY) !== 'false',
  setAnimationsEnabled: (enabled) => {
    localStorage.setItem(ANIMATIONS_KEY, String(enabled));
    // Toggle reduced motion class
    document.documentElement.classList.toggle('reduce-motion', !enabled);
    set({ animationsEnabled: enabled });
    debouncedApiSync({ animationsEnabled: enabled });
  },
  mapDefaultZoom: parseInt(localStorage.getItem(MAP_DEFAULT_ZOOM_KEY) || '15', 10),
  setMapDefaultZoom: (zoom) => {
    localStorage.setItem(MAP_DEFAULT_ZOOM_KEY, String(zoom));
    set({ mapDefaultZoom: zoom });
    debouncedApiSync({ mapDefaultZoom: zoom });
  },

  mapMaxZoom: parseInt(localStorage.getItem(MAP_MAX_ZOOM_KEY) || '20', 10),
  setMapMaxZoom: (zoom) => {
    localStorage.setItem(MAP_MAX_ZOOM_KEY, String(zoom));
    set({ mapMaxZoom: zoom });
    debouncedApiSync({ mapMaxZoom: zoom });
  },

  // Map default center
  mapDefaultCenter: (() => {
    try {
      const stored = localStorage.getItem(MAP_DEFAULT_CENTER_KEY);
      return stored ? JSON.parse(stored) : { latitude: 32.0853, longitude: 34.7818 }; // Default: Tel Aviv
    } catch {
      return { latitude: 32.0853, longitude: 34.7818 };
    }
  })(),
  setMapDefaultCenter: (center) => {
    localStorage.setItem(MAP_DEFAULT_CENTER_KEY, JSON.stringify(center));
    set({ mapDefaultCenter: center });
    debouncedApiSync({ mapDefaultCenter: center });
  },

  // Map default style
  mapDefaultStyle: (localStorage.getItem(MAP_DEFAULT_STYLE_KEY) as 'ROADMAP' | 'SATELLITE' | 'HYBRID' | 'TERRAIN' | 'DARK' | 'LIGHT') || 'DARK',
  setMapDefaultStyle: (style) => {
    localStorage.setItem(MAP_DEFAULT_STYLE_KEY, style);
    set({ mapDefaultStyle: style });
    debouncedApiSync({ mapDefaultStyle: style });
  },

  // Map tile provider
  mapDefaultTileProvider: (localStorage.getItem(MAP_DEFAULT_TILE_PROVIDER_KEY) as 'OSM' | 'ESRI' | 'GOOGLE' | 'CARTO') || 'OSM',
  setMapDefaultTileProvider: (provider) => {
    localStorage.setItem(MAP_DEFAULT_TILE_PROVIDER_KEY, provider);
    set({ mapDefaultTileProvider: provider });
    debouncedApiSync({ mapDefaultTileProvider: provider });
  },

  // Default floor plan for map
  mapDefaultFloorPlanId: localStorage.getItem(MAP_DEFAULT_FLOOR_PLAN_KEY) || null,
  setMapDefaultFloorPlanId: (floorPlanId) => {
    if (floorPlanId) {
      localStorage.setItem(MAP_DEFAULT_FLOOR_PLAN_KEY, floorPlanId);
    } else {
      localStorage.removeItem(MAP_DEFAULT_FLOOR_PLAN_KEY);
    }
    set({ mapDefaultFloorPlanId: floorPlanId });
    debouncedApiSync({ mapDefaultFloorPlanId: floorPlanId });
  },

  // Layer zoom settings
  mapLayerZoomSettings: (() => {
    try {
      const stored = localStorage.getItem(MAP_LAYER_ZOOM_SETTINGS_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  })(),
  setMapLayerZoomSettings: (settings) => {
    localStorage.setItem(MAP_LAYER_ZOOM_SETTINGS_KEY, JSON.stringify(settings));
    set({ mapLayerZoomSettings: settings });
    debouncedApiSync({ mapLayerZoomSettings: settings });
  },
  updateLayerZoomSetting: (layerId, setting) => {
    set((state) => {
      const newSettings = { ...state.mapLayerZoomSettings, [layerId]: setting };
      localStorage.setItem(MAP_LAYER_ZOOM_SETTINGS_KEY, JSON.stringify(newSettings));
      debouncedApiSync({ mapLayerZoomSettings: newSettings });
      return { mapLayerZoomSettings: newSettings };
    });
  },

  maxFovDepth: parseInt(localStorage.getItem(MAP_MAX_FOV_DEPTH_KEY) || '500', 10),
  setMaxFovDepth: (depth) => {
    localStorage.setItem(MAP_MAX_FOV_DEPTH_KEY, String(depth));
    set({ maxFovDepth: depth });
    debouncedApiSync({ maxFovDepth: depth });
  },

  selectionPlaybackSeconds: parseInt(localStorage.getItem(MAP_SELECTION_PLAYBACK_SEC_KEY) || '30', 10),
  setSelectionPlaybackSeconds: (seconds) => {
    localStorage.setItem(MAP_SELECTION_PLAYBACK_SEC_KEY, String(seconds));
    set({ selectionPlaybackSeconds: seconds });
    debouncedApiSync({ selectionPlaybackSeconds: seconds });
  },

  dateFormat: (localStorage.getItem(DATE_FORMAT_KEY) as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD') || 'MM/DD/YYYY',
  setDateFormat: (format) => {
    localStorage.setItem(DATE_FORMAT_KEY, format);
    set({ dateFormat: format });
    debouncedApiSync({ dateFormat: format });
  },
  timeFormat: (localStorage.getItem(TIME_FORMAT_KEY) as '12h' | '24h') || '24h',
  setTimeFormat: (format) => {
    localStorage.setItem(TIME_FORMAT_KEY, format);
    set({ timeFormat: format });
    debouncedApiSync({ timeFormat: format });
  },

  // Weather location
  weatherLocation: (() => {
    try {
      const stored = localStorage.getItem(WEATHER_LOCATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })(),
  setWeatherLocation: (location) => {
    if (location) {
      localStorage.setItem(WEATHER_LOCATION_KEY, JSON.stringify(location));
    } else {
      localStorage.removeItem(WEATHER_LOCATION_KEY);
    }
    set({ weatherLocation: location });
    debouncedApiSync({ weatherLocation: location });
  },

  // Alarm panel
  alarmPanelVisible: true,
  setAlarmPanelVisible: (visible) => set({ alarmPanelVisible: visible }),
  toggleAlarmPanel: () => set((state) => ({ alarmPanelVisible: !state.alarmPanelVisible })),
  alarmPanelPosition: (localStorage.getItem('commander-alarm-panel-position') as 'right' | 'bottom' | 'hidden') || 'right',
  setAlarmPanelPosition: (position) => {
    localStorage.setItem('commander-alarm-panel-position', position);
    set({ alarmPanelPosition: position });
    debouncedApiSync({ alarmPanelPosition: position });
  },

  // Location tree panel
  locationTreePanelVisible: false,
  setLocationTreePanelVisible: (visible) => set({ locationTreePanelVisible: visible }),
  toggleLocationTreePanel: () => set((state) => ({ locationTreePanelVisible: !state.locationTreePanelVisible })),

  // Live device monitor
  deviceMonitorVisible: false,
  setDeviceMonitorVisible: (visible) => set({ deviceMonitorVisible: visible }),
  toggleDeviceMonitor: () => set((state) => ({ deviceMonitorVisible: !state.deviceMonitorVisible })),

  // Video wall
  videoWallLayout: (localStorage.getItem('commander-video-wall-layout') as '1x1' | '2x2' | '3x3' | '4x4' | '1+5' | '1+7') || '2x2',
  setVideoWallLayout: (layout) => {
    localStorage.setItem('commander-video-wall-layout', layout);
    set({ videoWallLayout: layout });
    debouncedApiSync({ videoWallLayout: layout });
  },

  // Dashboard layout
  dashboardLayoutId: localStorage.getItem(LAYOUT_STORAGE_KEY) || 'command-center',
  setDashboardLayoutId: (layoutId) => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, layoutId);
    set({ dashboardLayoutId: layoutId });
    debouncedApiSync({ dashboardLayoutId: layoutId });
  },
  customLayouts: loadCustomLayouts(),
  addCustomLayout: (layout) => {
    set((state) => {
      const newLayouts = [...state.customLayouts, layout];
      saveCustomLayouts(newLayouts);
      debouncedApiSync({ customLayouts: newLayouts });
      return { customLayouts: newLayouts };
    });
  },
  updateCustomLayout: (layout) => {
    set((state) => {
      const newLayouts = state.customLayouts.map((l) =>
        l.id === layout.id ? layout : l
      );
      saveCustomLayouts(newLayouts);
      debouncedApiSync({ customLayouts: newLayouts });
      return { customLayouts: newLayouts };
    });
  },
  deleteCustomLayout: (layoutId) => {
    set((state) => {
      const newLayouts = state.customLayouts.filter((l) => l.id !== layoutId);
      saveCustomLayouts(newLayouts);
      debouncedApiSync({ customLayouts: newLayouts });
      if (state.dashboardLayoutId === layoutId) {
        localStorage.setItem(LAYOUT_STORAGE_KEY, 'command-center');
        debouncedApiSync({ dashboardLayoutId: 'command-center' });
        return { customLayouts: newLayouts, dashboardLayoutId: 'command-center' };
      }
      return { customLayouts: newLayouts };
    });
  },

  // Module settings
  moduleSettings: loadModuleSettings(),
  getModuleSettings: (layoutId, moduleId) => {
    const key = `${layoutId}:${moduleId}`;
    const s = get();
    return s.moduleSettings[key];
  },
  setModuleSettings: (layoutId, moduleId, settings) => {
    const key = `${layoutId}:${moduleId}`;
    set((state) => {
      const newSettings = { ...state.moduleSettings, [key]: settings };
      saveModuleSettings(newSettings);
      debouncedApiSync({ moduleSettings: newSettings });
      return { moduleSettings: newSettings };
    });
  },
  clearModuleSettings: (layoutId) => {
    set((state) => {
      if (!layoutId) {
        saveModuleSettings({});
        debouncedApiSync({ moduleSettings: {} });
        return { moduleSettings: {} };
      }
      const newSettings = Object.fromEntries(
        Object.entries(state.moduleSettings).filter(([key]) => !key.startsWith(`${layoutId}:`))
      );
      saveModuleSettings(newSettings);
      debouncedApiSync({ moduleSettings: newSettings });
      return { moduleSettings: newSettings };
    });
  },

  // Modal state
  activeModal: null,
  modalData: null,
  openModal: (modalId, data) => set({ activeModal: modalId, modalData: data }),
  closeModal: () => set({ activeModal: null, modalData: null }),

  // Notifications
  unreadNotifications: 0,
  setUnreadNotifications: (count) => set({ unreadNotifications: count }),
  incrementNotifications: () => set((state) => ({ unreadNotifications: state.unreadNotifications + 1 })),
  clearNotifications: () => set({ unreadNotifications: 0 }),

  // Sound
  soundEnabled: true,
  setSoundEnabled: (enabled) => {
    set({ soundEnabled: enabled });
    debouncedApiSync({ soundEnabled: enabled });
  },
  soundMuted: false,
  toggleSoundMuted: () => set((state) => ({ soundMuted: !state.soundMuted })),

  // Incident Placement Mode (transient, not persisted)
  incidentPlacementMode: false,
  incidentPlacementType: 'point' as const,
  setIncidentPlacementMode: (active) => set({ incidentPlacementMode: active, ...(active ? {} : { incidentPlacementType: 'point' as const }) }),
  setIncidentPlacementType: (type) => set({ incidentPlacementType: type }),
  toggleIncidentPlacementMode: () => set((state) => ({ incidentPlacementMode: !state.incidentPlacementMode, ...(!state.incidentPlacementMode ? {} : { incidentPlacementType: 'point' as const }) })),

  // Global incident-creation dialog
  incidentDialogPrefill: null,
  pendingIncidentAttachment: null,
  openIncidentDialogWithPrefill: (prefill, attachment) => {
    // Opening the dialog ends placement mode — the operator either
    // dropped the pin or picked a tile, the crosshair is no longer
    // needed.
    set({
      incidentDialogPrefill: prefill,
      pendingIncidentAttachment: attachment ?? null,
      incidentPlacementMode: false,
      incidentPlacementType: 'point' as const,
    });
  },
  closeIncidentDialog: () => {
    // Revoke any blob URL we created for the dialog thumbnail so it
    // doesn't pin the bytes in memory after close.
    const url = get().incidentDialogPrefill?.snapshotDataUrl;
    if (url && url.startsWith('blob:')) {
      try { URL.revokeObjectURL(url); } catch { /* nothing to do */ }
    }
    set({ incidentDialogPrefill: null, pendingIncidentAttachment: null });
  },

  // Persistent Alert Queue (G2)
  pendingAlerts: [],
  addPendingAlert: (alert) => set((state) => ({
    pendingAlerts: [
      ...state.pendingAlerts,
      { ...alert, createdAt: new Date().toISOString(), acknowledged: false },
    ],
  })),
  acknowledgePendingAlert: (alertId) => set((state) => ({
    pendingAlerts: state.pendingAlerts.map((a) =>
      a.id === alertId ? { ...a, acknowledged: true } : a
    ),
  })),
  acknowledgeAllPendingAlerts: () => set((state) => ({
    pendingAlerts: state.pendingAlerts.map((a) => ({ ...a, acknowledged: true })),
  })),

  // Voice Commands
  voiceCommandsEnabled: localStorage.getItem('commander-voice-enabled') !== 'false',
  setVoiceCommandsEnabled: (enabled) => {
    localStorage.setItem('commander-voice-enabled', String(enabled));
    set({ voiceCommandsEnabled: enabled });
    debouncedApiSync({ voiceCommandsEnabled: enabled });
  },
  voiceCommandAutoStop: localStorage.getItem('commander-voice-autostop') !== 'false',
  setVoiceCommandAutoStop: (autoStop) => {
    localStorage.setItem('commander-voice-autostop', String(autoStop));
    set({ voiceCommandAutoStop: autoStop });
    debouncedApiSync({ voiceCommandAutoStop: autoStop });
  },
  voiceCommandsPosition: (localStorage.getItem('commander-voice-position') as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left') || 'bottom-right',
  setVoiceCommandsPosition: (position) => {
    localStorage.setItem('commander-voice-position', position);
    set({ voiceCommandsPosition: position });
    debouncedApiSync({ voiceCommandsPosition: position });
  },

  // Chat Notifications
  chatToastEnabled: localStorage.getItem('commander-chat-toast') !== 'false',
  setChatToastEnabled: (enabled) => {
    localStorage.setItem('commander-chat-toast', String(enabled));
    set({ chatToastEnabled: enabled });
    debouncedApiSync({ chatToastEnabled: enabled });
  },
  chatSoundEnabled: localStorage.getItem('commander-chat-sound') !== 'false',
  setChatSoundEnabled: (enabled) => {
    localStorage.setItem('commander-chat-sound', String(enabled));
    set({ chatSoundEnabled: enabled });
    debouncedApiSync({ chatSoundEnabled: enabled });
  },
  chatSoundType: localStorage.getItem('commander-chat-sound-type') || 'chime',
  setChatSoundType: (soundType) => {
    localStorage.setItem('commander-chat-sound-type', soundType);
    set({ chatSoundType: soundType });
    debouncedApiSync({ chatSoundType: soundType });
  },
  chatEnabled: localStorage.getItem('commander-chat-enabled') !== 'false',
  setChatEnabled: (enabled) => {
    localStorage.setItem('commander-chat-enabled', String(enabled));
    set({ chatEnabled: enabled });
    debouncedApiSync({ chatEnabled: enabled });
  },
  chatPosition: (localStorage.getItem('commander-chat-position') as 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left') || 'bottom-right',
  setChatPosition: (position) => {
    localStorage.setItem('commander-chat-position', position);
    set({ chatPosition: position });
    debouncedApiSync({ chatPosition: position });
  },

  // Incident Notifications
  desktopNotificationsEnabled: localStorage.getItem('commander-desktop-notifications') !== 'false',
  setDesktopNotificationsEnabled: (enabled) => {
    localStorage.setItem('commander-desktop-notifications', String(enabled));
    set({ desktopNotificationsEnabled: enabled });
    debouncedApiSync({ desktopNotificationsEnabled: enabled });
  },
  incidentToastEnabled: localStorage.getItem('commander-incident-toast') !== 'false',
  setIncidentToastEnabled: (enabled) => {
    localStorage.setItem('commander-incident-toast', String(enabled));
    set({ incidentToastEnabled: enabled });
    debouncedApiSync({ incidentToastEnabled: enabled });
  },

  // Incident Toast Configuration
  incidentToastPosition: (localStorage.getItem('commander-incident-toast-position') as UIState['incidentToastPosition']) || 'top-right',
  setIncidentToastPosition: (position) => {
    localStorage.setItem('commander-incident-toast-position', position);
    set({ incidentToastPosition: position });
    debouncedApiSync({ incidentToastPosition: position });
  },
  incidentToastFields: (() => {
    try {
      const saved = localStorage.getItem('commander-incident-toast-fields');
      return saved ? JSON.parse(saved) : { showPriority: true, showCategory: true, showDevice: true, showLocation: true, showStatus: true, showTime: true, showSource: false, showAssignee: false };
    } catch { return { showPriority: true, showCategory: true, showDevice: true, showLocation: true, showStatus: true, showTime: true, showSource: false, showAssignee: false }; }
  })(),
  setIncidentToastFields: (fields) => {
    const updated = { ...get().incidentToastFields, ...fields };
    localStorage.setItem('commander-incident-toast-fields', JSON.stringify(updated));
    set({ incidentToastFields: updated });
    debouncedApiSync({ incidentToastFields: updated });
  },
  incidentToastDuration: parseInt(localStorage.getItem('commander-incident-toast-duration') || '0', 10) || 0,
  setIncidentToastDuration: (ms) => {
    localStorage.setItem('commander-incident-toast-duration', String(ms));
    set({ incidentToastDuration: ms });
    debouncedApiSync({ incidentToastDuration: ms });
  },

  // Device Display
  treeLibraryDisplay: localStorage.getItem('commander-tree-library-display') !== 'false',
  setTreeLibraryDisplay: (enabled) => {
    localStorage.setItem('commander-tree-library-display', String(enabled));
    set({ treeLibraryDisplay: enabled });
  },
  badgeVisualizationMode: (localStorage.getItem('commander-badge-mode') as 'ring' | 'side_badges' | 'status_bar' | 'orbit') || 'ring',
  setBadgeVisualizationMode: (mode) => {
    localStorage.setItem('commander-badge-mode', mode);
    set({ badgeVisualizationMode: mode });
    debouncedApiSync({ badgeVisualizationMode: mode });
  },

  // Incident Badges on Device Markers
  incidentBadgesEnabled: localStorage.getItem('commander-incident-badges') !== 'false',
  setIncidentBadgesEnabled: (enabled) => {
    localStorage.setItem('commander-incident-badges', String(enabled));
    set({ incidentBadgesEnabled: enabled });
    debouncedApiSync({ incidentBadgesEnabled: enabled });
  },
  incidentBadgeShowPriority: localStorage.getItem('commander-incident-badge-priority') !== 'false',
  setIncidentBadgeShowPriority: (show) => {
    localStorage.setItem('commander-incident-badge-priority', String(show));
    set({ incidentBadgeShowPriority: show });
    debouncedApiSync({ incidentBadgeShowPriority: show });
  },
  incidentBadgeShowCount: localStorage.getItem('commander-incident-badge-count') !== 'false',
  setIncidentBadgeShowCount: (show) => {
    localStorage.setItem('commander-incident-badge-count', String(show));
    set({ incidentBadgeShowCount: show });
    debouncedApiSync({ incidentBadgeShowCount: show });
  },
  incidentBadgeAnimation: localStorage.getItem('commander-incident-badge-anim') !== 'false',
  setIncidentBadgeAnimation: (enabled) => {
    localStorage.setItem('commander-incident-badge-anim', String(enabled));
    set({ incidentBadgeAnimation: enabled });
    debouncedApiSync({ incidentBadgeAnimation: enabled });
  },

  // Configurable Hover Tooltip Fields
  deviceHoverFields: JSON.parse(localStorage.getItem('commander-device-hover-fields') || JSON.stringify({
    showName: true,
    showCategory: true,
    showState: true,
    showBadges: true,
    showDriver: false,
    showLocation: false,
    showDeviceId: false,
    showLastEvent: false,
    showIncidentCount: true,
  })),
  setDeviceHoverFields: (fields) => {
    const merged = { ...get().deviceHoverFields, ...fields };
    localStorage.setItem('commander-device-hover-fields', JSON.stringify(merged));
    set({ deviceHoverFields: merged });
    debouncedApiSync({ deviceHoverFields: merged });
  },

  // Configurable Incident Hover Tooltip Fields (AlarmMarkerLayer)
  incidentHoverFields: JSON.parse(localStorage.getItem('commander-incident-hover-fields') || JSON.stringify({
    showTitle: true,
    showPriority: true,
    showStatus: true,
    showCategory: true,
    showIncidentNumber: true,
    showLocation: true,
    showTimestamp: true,
    showType: false,
    showAssignee: false,
    showIncidentCount: true,
  })),
  setIncidentHoverFields: (fields) => {
    const merged = { ...get().incidentHoverFields, ...fields };
    localStorage.setItem('commander-incident-hover-fields', JSON.stringify(merged));
    set({ incidentHoverFields: merged });
    debouncedApiSync({ incidentHoverFields: merged });
  },

  // SOP Display Mode
  sopDisplayMode: (localStorage.getItem('commander-sop-display-mode') as 'inline' | 'wizard' | 'side-panel') || 'inline',
  setSopDisplayMode: (mode) => {
    localStorage.setItem('commander-sop-display-mode', mode);
    set({ sopDisplayMode: mode });
    debouncedApiSync({ sopDisplayMode: mode });
  },

  // API sync state
  _synced: false,
  _syncInProgress: false,
  _initFromApi: async () => {
    if (get()._syncInProgress) return;
    set({ _syncInProgress: true });
    try {
      const apiSettings = await userPreferencesApi.getUiSettings();
      if (apiSettings && Object.keys(apiSettings).length > 0) {
        // API data exists — apply to store + localStorage (API is source of truth)
        const updates: Partial<UIState> = {};

        if (apiSettings.theme !== undefined) { updates.theme = apiSettings.theme as UIState['theme']; localStorage.setItem('commander-theme', apiSettings.theme as string); }
        // Language is a GLOBAL per-user/device choice, NOT per-tenant. Preferences
        // are stored per-tenant server-side, so applying the fetched language on
        // every _initFromApi() would flip the UI on org switch (each tenant has its
        // own saved locale). Only seed from the server when the user has made NO
        // local choice yet (fresh device / first login); once localStorage has a
        // language, that wins and switching customers never changes it.
        if (apiSettings.language !== undefined && !localStorage.getItem(LANGUAGE_LOCALSTORAGE_KEY)) {
          const lng = apiSettings.language as SupportedLanguage;
          updates.language = lng;
          localStorage.setItem(LANGUAGE_LOCALSTORAGE_KEY, lng);
          applyDirAndLang(lng);
          if (i18n.language !== lng) void i18n.changeLanguage(lng);
        }
        if (apiSettings.accentColor !== undefined) { updates.accentColor = apiSettings.accentColor as UIState['accentColor']; localStorage.setItem(ACCENT_COLOR_KEY, apiSettings.accentColor as string); }
        if (apiSettings.uiDensity !== undefined) { updates.uiDensity = apiSettings.uiDensity as UIState['uiDensity']; localStorage.setItem(UI_DENSITY_KEY, apiSettings.uiDensity as string); }
        if (apiSettings.tablePageSize !== undefined) { updates.tablePageSize = apiSettings.tablePageSize as number; localStorage.setItem(TABLE_PAGE_SIZE_KEY, String(apiSettings.tablePageSize)); }
        if (apiSettings.animationsEnabled !== undefined) { updates.animationsEnabled = apiSettings.animationsEnabled as boolean; localStorage.setItem(ANIMATIONS_KEY, String(apiSettings.animationsEnabled)); }
        if (apiSettings.sidebarAutoClose !== undefined) { updates.sidebarAutoClose = apiSettings.sidebarAutoClose as boolean; localStorage.setItem(SIDEBAR_AUTOCLOSE_KEY, String(apiSettings.sidebarAutoClose)); }
        if (apiSettings.mapDefaultZoom !== undefined) { updates.mapDefaultZoom = apiSettings.mapDefaultZoom as number; localStorage.setItem(MAP_DEFAULT_ZOOM_KEY, String(apiSettings.mapDefaultZoom)); }
        if (apiSettings.mapMaxZoom !== undefined) { updates.mapMaxZoom = apiSettings.mapMaxZoom as number; localStorage.setItem(MAP_MAX_ZOOM_KEY, String(apiSettings.mapMaxZoom)); }
        if (apiSettings.mapDefaultCenter !== undefined) { updates.mapDefaultCenter = apiSettings.mapDefaultCenter as UIState['mapDefaultCenter']; localStorage.setItem(MAP_DEFAULT_CENTER_KEY, JSON.stringify(apiSettings.mapDefaultCenter)); }
        if (apiSettings.mapDefaultStyle !== undefined) { updates.mapDefaultStyle = apiSettings.mapDefaultStyle as UIState['mapDefaultStyle']; localStorage.setItem(MAP_DEFAULT_STYLE_KEY, apiSettings.mapDefaultStyle as string); }
        if (apiSettings.mapDefaultTileProvider !== undefined) { updates.mapDefaultTileProvider = apiSettings.mapDefaultTileProvider as UIState['mapDefaultTileProvider']; localStorage.setItem(MAP_DEFAULT_TILE_PROVIDER_KEY, apiSettings.mapDefaultTileProvider as string); }
        if (apiSettings.mapDefaultFloorPlanId !== undefined) { updates.mapDefaultFloorPlanId = apiSettings.mapDefaultFloorPlanId as string | null; if (apiSettings.mapDefaultFloorPlanId) localStorage.setItem(MAP_DEFAULT_FLOOR_PLAN_KEY, apiSettings.mapDefaultFloorPlanId as string); else localStorage.removeItem(MAP_DEFAULT_FLOOR_PLAN_KEY); }
        if (apiSettings.mapLayerZoomSettings !== undefined) { updates.mapLayerZoomSettings = apiSettings.mapLayerZoomSettings as UIState['mapLayerZoomSettings']; localStorage.setItem(MAP_LAYER_ZOOM_SETTINGS_KEY, JSON.stringify(apiSettings.mapLayerZoomSettings)); }
        if (apiSettings.maxFovDepth !== undefined) { updates.maxFovDepth = apiSettings.maxFovDepth as number; localStorage.setItem(MAP_MAX_FOV_DEPTH_KEY, String(apiSettings.maxFovDepth)); }
        if (apiSettings.selectionPlaybackSeconds !== undefined) { updates.selectionPlaybackSeconds = apiSettings.selectionPlaybackSeconds as number; localStorage.setItem(MAP_SELECTION_PLAYBACK_SEC_KEY, String(apiSettings.selectionPlaybackSeconds)); }
        if (apiSettings.dateFormat !== undefined) { updates.dateFormat = apiSettings.dateFormat as UIState['dateFormat']; localStorage.setItem(DATE_FORMAT_KEY, apiSettings.dateFormat as string); }
        if (apiSettings.timeFormat !== undefined) { updates.timeFormat = apiSettings.timeFormat as UIState['timeFormat']; localStorage.setItem(TIME_FORMAT_KEY, apiSettings.timeFormat as string); }
        if (apiSettings.weatherLocation !== undefined) { updates.weatherLocation = apiSettings.weatherLocation as UIState['weatherLocation']; if (apiSettings.weatherLocation) localStorage.setItem(WEATHER_LOCATION_KEY, JSON.stringify(apiSettings.weatherLocation)); else localStorage.removeItem(WEATHER_LOCATION_KEY); }
        if (apiSettings.alarmPanelPosition !== undefined) { updates.alarmPanelPosition = apiSettings.alarmPanelPosition as UIState['alarmPanelPosition']; localStorage.setItem('commander-alarm-panel-position', apiSettings.alarmPanelPosition as string); }
        if (apiSettings.videoWallLayout !== undefined) { updates.videoWallLayout = apiSettings.videoWallLayout as UIState['videoWallLayout']; localStorage.setItem('commander-video-wall-layout', apiSettings.videoWallLayout as string); }
        if (apiSettings.dashboardLayoutId !== undefined) { updates.dashboardLayoutId = apiSettings.dashboardLayoutId as string; localStorage.setItem(LAYOUT_STORAGE_KEY, apiSettings.dashboardLayoutId as string); }
        // Validate API-supplied layouts/settings before they reach the
        // dashboard renderer. A corrupt layout in the DB must NOT be able
        // to freeze the app — the same sanitizers guard both the
        // localStorage and the API path.
        if (apiSettings.customLayouts !== undefined) {
          const safeLayouts = sanitizeCustomLayouts(apiSettings.customLayouts);
          updates.customLayouts = safeLayouts;
          localStorage.setItem(CUSTOM_LAYOUTS_KEY, JSON.stringify(safeLayouts));
        }
        if (apiSettings.moduleSettings !== undefined) {
          const safeSettings = sanitizeModuleSettings(apiSettings.moduleSettings);
          updates.moduleSettings = safeSettings;
          localStorage.setItem(MODULE_SETTINGS_KEY, JSON.stringify(safeSettings));
        }
        if (apiSettings.soundEnabled !== undefined) { updates.soundEnabled = apiSettings.soundEnabled as boolean; }
        if (apiSettings.voiceCommandsEnabled !== undefined) { updates.voiceCommandsEnabled = apiSettings.voiceCommandsEnabled as boolean; localStorage.setItem('commander-voice-enabled', String(apiSettings.voiceCommandsEnabled)); }
        if (apiSettings.voiceCommandAutoStop !== undefined) { updates.voiceCommandAutoStop = apiSettings.voiceCommandAutoStop as boolean; localStorage.setItem('commander-voice-autostop', String(apiSettings.voiceCommandAutoStop)); }
        if (apiSettings.voiceCommandsPosition !== undefined) { updates.voiceCommandsPosition = apiSettings.voiceCommandsPosition as UIState['voiceCommandsPosition']; localStorage.setItem('commander-voice-position', apiSettings.voiceCommandsPosition as string); }
        if (apiSettings.chatToastEnabled !== undefined) { updates.chatToastEnabled = apiSettings.chatToastEnabled as boolean; localStorage.setItem('commander-chat-toast', String(apiSettings.chatToastEnabled)); }
        if (apiSettings.chatSoundEnabled !== undefined) { updates.chatSoundEnabled = apiSettings.chatSoundEnabled as boolean; localStorage.setItem('commander-chat-sound', String(apiSettings.chatSoundEnabled)); }
        if (apiSettings.chatSoundType !== undefined) { updates.chatSoundType = apiSettings.chatSoundType as string; localStorage.setItem('commander-chat-sound-type', apiSettings.chatSoundType as string); }
        if (apiSettings.chatEnabled !== undefined) { updates.chatEnabled = apiSettings.chatEnabled as boolean; localStorage.setItem('commander-chat-enabled', String(apiSettings.chatEnabled)); }
        if (apiSettings.chatPosition !== undefined) { updates.chatPosition = apiSettings.chatPosition as UIState['chatPosition']; localStorage.setItem('commander-chat-position', apiSettings.chatPosition as string); }
        if (apiSettings.desktopNotificationsEnabled !== undefined) { updates.desktopNotificationsEnabled = apiSettings.desktopNotificationsEnabled as boolean; localStorage.setItem('commander-desktop-notifications', String(apiSettings.desktopNotificationsEnabled)); }
        if (apiSettings.incidentToastEnabled !== undefined) { updates.incidentToastEnabled = apiSettings.incidentToastEnabled as boolean; localStorage.setItem('commander-incident-toast', String(apiSettings.incidentToastEnabled)); }
        if (apiSettings.incidentToastPosition !== undefined) { updates.incidentToastPosition = apiSettings.incidentToastPosition as UIState['incidentToastPosition']; localStorage.setItem('commander-incident-toast-position', apiSettings.incidentToastPosition as string); }
        if (apiSettings.incidentToastFields !== undefined) { updates.incidentToastFields = apiSettings.incidentToastFields as UIState['incidentToastFields']; localStorage.setItem('commander-incident-toast-fields', JSON.stringify(apiSettings.incidentToastFields)); }
        if (apiSettings.incidentToastDuration !== undefined) { updates.incidentToastDuration = apiSettings.incidentToastDuration as number; localStorage.setItem('commander-incident-toast-duration', String(apiSettings.incidentToastDuration)); }
        if (apiSettings.badgeVisualizationMode !== undefined) { updates.badgeVisualizationMode = apiSettings.badgeVisualizationMode as UIState['badgeVisualizationMode']; localStorage.setItem('commander-badge-mode', apiSettings.badgeVisualizationMode as string); }
        if (apiSettings.incidentBadgesEnabled !== undefined) { updates.incidentBadgesEnabled = apiSettings.incidentBadgesEnabled as boolean; localStorage.setItem('commander-incident-badges', String(apiSettings.incidentBadgesEnabled)); }
        if (apiSettings.incidentBadgeShowPriority !== undefined) { updates.incidentBadgeShowPriority = apiSettings.incidentBadgeShowPriority as boolean; localStorage.setItem('commander-incident-badge-priority', String(apiSettings.incidentBadgeShowPriority)); }
        if (apiSettings.incidentBadgeShowCount !== undefined) { updates.incidentBadgeShowCount = apiSettings.incidentBadgeShowCount as boolean; localStorage.setItem('commander-incident-badge-count', String(apiSettings.incidentBadgeShowCount)); }
        if (apiSettings.incidentBadgeAnimation !== undefined) { updates.incidentBadgeAnimation = apiSettings.incidentBadgeAnimation as boolean; localStorage.setItem('commander-incident-badge-anim', String(apiSettings.incidentBadgeAnimation)); }
        if (apiSettings.deviceHoverFields !== undefined) { updates.deviceHoverFields = apiSettings.deviceHoverFields as UIState['deviceHoverFields']; localStorage.setItem('commander-device-hover-fields', JSON.stringify(apiSettings.deviceHoverFields)); }
        if (apiSettings.incidentHoverFields !== undefined) { updates.incidentHoverFields = apiSettings.incidentHoverFields as UIState['incidentHoverFields']; localStorage.setItem('commander-incident-hover-fields', JSON.stringify(apiSettings.incidentHoverFields)); }
        if (apiSettings.sopDisplayMode !== undefined) { updates.sopDisplayMode = apiSettings.sopDisplayMode as UIState['sopDisplayMode']; localStorage.setItem('commander-sop-display-mode', apiSettings.sopDisplayMode as string); }
        if (apiSettings.starredIncidents !== undefined) { localStorage.setItem('commander_starred_incidents', JSON.stringify(apiSettings.starredIncidents)); }
        if (apiSettings.videoWallSettings !== undefined) { localStorage.setItem('commander-video-wall-settings', JSON.stringify(apiSettings.videoWallSettings)); }
        if (apiSettings.videoWallLayouts !== undefined) { localStorage.setItem('commander-video-wall-layouts', JSON.stringify(apiSettings.videoWallLayouts)); }
        if (apiSettings.incidentCommandConfig !== undefined) { localStorage.setItem('commander-incident-command-config', JSON.stringify(apiSettings.incidentCommandConfig)); }
        if (apiSettings.floorPlanFavorites !== undefined) { localStorage.setItem('floorPlan.favorites', JSON.stringify(apiSettings.floorPlanFavorites)); }
        if (apiSettings.floorPlanRecentlyUsed !== undefined) { localStorage.setItem('floorPlan.recentlyUsed', JSON.stringify(apiSettings.floorPlanRecentlyUsed)); }
        if (apiSettings.deviceLibraryFavorites !== undefined) { localStorage.setItem('deviceLibrary.favorites', JSON.stringify(apiSettings.deviceLibraryFavorites)); }
        if (apiSettings.deviceLibraryRecentlyUsed !== undefined) { localStorage.setItem('deviceLibrary.recentlyUsed', JSON.stringify(apiSettings.deviceLibraryRecentlyUsed)); }
        if (apiSettings.voiceTtsSettings !== undefined) {
          const vts = apiSettings.voiceTtsSettings as Record<string, string>;
          for (const [k, v] of Object.entries(vts)) localStorage.setItem(`commander-voice-${k}`, v);
        }
        if (apiSettings.settingsExpandedGroups !== undefined) { localStorage.setItem('settings-expanded-groups', JSON.stringify(apiSettings.settingsExpandedGroups)); }
        if (apiSettings.popoutWindows !== undefined) { localStorage.setItem('commander-popout-windows', JSON.stringify(apiSettings.popoutWindows)); }

        set({ ...updates, _synced: true, _syncInProgress: false });
      } else {
        // No API data — push current localStorage state to API (one-time migration)
        const s = get();
        const blob: Record<string, unknown> = {
          theme: s.theme, language: s.language, accentColor: s.accentColor, uiDensity: s.uiDensity,
          tablePageSize: s.tablePageSize, animationsEnabled: s.animationsEnabled,
          sidebarAutoClose: s.sidebarAutoClose, mapDefaultZoom: s.mapDefaultZoom, mapMaxZoom: s.mapMaxZoom,
          mapDefaultCenter: s.mapDefaultCenter, mapDefaultStyle: s.mapDefaultStyle,
          mapDefaultTileProvider: s.mapDefaultTileProvider, mapDefaultFloorPlanId: s.mapDefaultFloorPlanId,
          mapLayerZoomSettings: s.mapLayerZoomSettings, maxFovDepth: s.maxFovDepth, selectionPlaybackSeconds: s.selectionPlaybackSeconds, dateFormat: s.dateFormat,
          timeFormat: s.timeFormat, weatherLocation: s.weatherLocation,
          alarmPanelPosition: s.alarmPanelPosition, videoWallLayout: s.videoWallLayout,
          dashboardLayoutId: s.dashboardLayoutId, customLayouts: s.customLayouts,
          moduleSettings: s.moduleSettings, soundEnabled: s.soundEnabled,
          voiceCommandsEnabled: s.voiceCommandsEnabled, voiceCommandAutoStop: s.voiceCommandAutoStop,
          voiceCommandsPosition: s.voiceCommandsPosition, chatToastEnabled: s.chatToastEnabled,
          chatSoundEnabled: s.chatSoundEnabled, chatSoundType: s.chatSoundType,
          chatEnabled: s.chatEnabled, chatPosition: s.chatPosition,
          desktopNotificationsEnabled: s.desktopNotificationsEnabled,
          incidentToastEnabled: s.incidentToastEnabled, incidentToastPosition: s.incidentToastPosition,
          incidentToastFields: s.incidentToastFields, incidentToastDuration: s.incidentToastDuration,
          badgeVisualizationMode: s.badgeVisualizationMode,
          incidentBadgesEnabled: s.incidentBadgesEnabled, incidentBadgeShowPriority: s.incidentBadgeShowPriority,
          incidentBadgeShowCount: s.incidentBadgeShowCount, incidentBadgeAnimation: s.incidentBadgeAnimation,
          deviceHoverFields: s.deviceHoverFields, incidentHoverFields: s.incidentHoverFields,
          sopDisplayMode: s.sopDisplayMode,
        };
        try { await userPreferencesApi.updateUiSettings(blob); } catch { /* non-fatal */ }
        set({ _synced: true, _syncInProgress: false });
      }
    } catch (err) {
      console.warn('[UIStore] Failed to load settings from API, using localStorage cache:', err);
      set({ _syncInProgress: false });
    }
  },
}));
